/**
 * AiAssistantSteps — что делает (или сделал) агент.
 *
 * Два режима:
 *   • live — запрос ещё выполняется: список шагов плана со статусами
 *     pending / running / done и подпись текущей фазы;
 *   • done — шаги последнего ответа: что именно было выполнено, за сколько
 *     и с каким результатом.
 *
 * Данные приходят с сервера: для live — /api/progress, для done — блок
 * `steps` в ленте ответа (он же сохраняется в БД).
 */
import { useTranslation } from "react-i18next";
import {
    IoCheckmarkCircleOutline,
    IoCloseCircleOutline,
    IoEllipseOutline,
    IoSyncOutline,
} from "react-icons/io5";

function StepRow({ step, live }) {
    // В живом списке на финальной фазе приходят уже выполненные шаги без
    // поля state — без этого они выглядели бы как «в ожидании».
    const hasResult = step.ok !== undefined && step.ok !== null;
    const state = String(step.state || (hasResult ? (step.ok ? "done" : "failed") : "")).toLowerCase();
    const ok = step.ok !== false;
    const failed = state === "failed" || (state === "done" && step.ok === false);
    const running = live && state === "running";
    const pending = live && (!state || state === "pending");

    let icon = <IoEllipseOutline size={13} />;
    if (failed || (!live && !ok)) icon = <IoCloseCircleOutline size={13} />;
    else if (running) icon = <IoSyncOutline size={13} className="aia-step__spin" />;
    else if (!live) icon = <IoCheckmarkCircleOutline size={13} />;
    else if (state === "done") icon = <IoCheckmarkCircleOutline size={13} />;

    const modifier = failed || (!live && !ok)
        ? " is-failed"
        : running
            ? " is-running"
            : pending
                ? " is-pending"
                : " is-done";

    return (
        <li className={`aia-step${modifier}${step.mutating ? " is-mutating" : ""}`}>
            <span className="aia-step__icon">{icon}</span>
            <span className="aia-step__label">{step.label || step.tool}</span>
            {!live && Number(step.ms) > 0 ? (
                <span className="aia-step__ms">{Number(step.ms)} мс</span>
            ) : null}
            {!live && !ok && step.error ? (
                <span className="aia-step__error">{step.error}</span>
            ) : null}
        </li>
    );
}

export function AiAssistantStepsDone({ items }) {
    const { t } = useTranslation("aiAssistant");
    if (!Array.isArray(items) || items.length === 0) return null;

    const failed = items.filter((step) => step.ok === false).length;

    return (
        <div className="aia-steps aia-steps--done">
            <div className="aia-steps__head aia-steps__head--done">
                <IoCheckmarkCircleOutline size={13} className="aia-steps__headIcon" />
                {t("steps.done")}
                <span className="aia-steps__count">
                    {failed ? `${items.length - failed}/${items.length}` : items.length}
                </span>
            </div>
            <ul className="aia-steps__list">
                {items.map((step, index) => (
                    <StepRow key={`${step.tool}-${index}`} step={step} live={false} />
                ))}
            </ul>
        </div>
    );
}

export function AiAssistantStepsLive({ progress }) {
    const { t } = useTranslation("aiAssistant");
    if (!progress) return null;

    const steps = Array.isArray(progress.steps) ? progress.steps : [];
    const total = steps.length;
    const done = steps.filter((step) => {
        const state = String(step.state || "").toLowerCase();
        if (state === "done") return true;
        // На фазе ответа статус не передаётся, но результат уже есть.
        return !state && step.ok !== undefined && step.ok !== null;
    }).length;
    const phase = String(progress.phase || "");

    // Заголовок НЕ меняется на название текущего шага.
    // Раньше он бежал «ищу → добавляю → открываю», и список читался как
    // отдельные события, а не как один перечень действий.
    const working = phase === "answer";
    const caption = working
        ? t("steps.answer")
        : total
            ? t("steps.live")
            : phase === "plan"
                ? t("steps.plan")
                : t("hint.thinking");

    return (
        <div className="aia-steps aia-steps--live">
            <div className="aia-steps__head aia-steps__head--live">
                {working ? null : <span className="aia-steps__pulse" />}
                {caption}
                {total ? (
                    <span className="aia-steps__count">
                        {done}/{total}
                    </span>
                ) : null}
            </div>
            {total ? (
                <ul className="aia-steps__list">
                    {steps.map((step, index) => (
                        <StepRow key={`${step.tool}-${index}`} step={step} live />
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

export default AiAssistantStepsDone;
